import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import Switch from "../../../../components/form/Switch";
import Badge from "../../../../components/elements/Badge";
import {
  emailTemplateList,
  emailTemplateStatusChange,
} from "../../../../services/emailService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ManageEmailTemplateDrawer from "../../../../components/sections/admin/emailTemplate/drawer/ManageEmailTemplateDrawer";
import Select from "../../../../components/form/select/Select";
import Button from "../../../../components/form/Button";
import { t } from "i18next";

const EmailTemplate = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const emailName = {
    send_otp: "Send Otp",
    welcome_email: "Welcome Email",
    reset_pass_link_invite: "Reset Password",
    donation: "Donation",
    send_contact_us_rec: "Contact Us",
    email_change: "Change Email",
    scholarship_submission: "Scholarship Submission",
    application_status_track: "Application Status Track",
    application_status_reminder: "Application Status Reminder",
    donor_send_invite: "Donor Send Invite",
    contact_us_admin_notify: "Contact Us Admin Notify",
    user_account_delete: "User Account Delete",
    scholarship_submission_admin_notify: "Scholarship Submission Admin Notify",
    send_message: "Send Message",
    donation_received: "Donation Received",
  };

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["emailtemplate", filters, search],
    queryFn: async () => {
      const res = await emailTemplateList({
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //   Application Form status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(
          ["emailtemplate", filters, search],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || []).map((elm) =>
                elm?._id === id
                  ? {
                      ...elm,
                      status: status,
                    }
                  : elm
              );
              return { ...state, data: stateData };
            }
          }
        );
        const res = await emailTemplateStatusChange({
          ids: [id],
          status: status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          queryClient.setQueryData(
            ["emailtemplate", filters, search],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = (stateData || []).map((elm) =>
                  elm?._id === payload?.id
                    ? {
                        ...elm,
                        status:
                          elm?.status === "active" ? "deactivated" : "active",
                      }
                    : elm
                );
                return { ...state, data: stateData };
              }
            }
          );
          toast.warning(res?.message);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const tableHeadData = [
    {
      _id: "name",
      name: "name",
      align: "left",
    },
    {
      _id: 1,
      name: "Email For",
      align: "left",
    },
    // {
    //   _id: 2,
    //   name: "Subject",
    //   align: "left",
    // },
    {
      _id: 3,
      name: "From Name",
      align: "left",
    },
    {
      _id: 4,
      name: "Status",
      align: "left",
    },
    {
      _id: 5,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "-",
        },
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: emailName[item?.code] || "-",
        },
        // {
        //   _id: 2,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data:
        //     (
        //       <div
        //         dangerouslySetInnerHTML={{
        //           __html: item?.subject || "-",
        //         }}
        //       ></div>
        //     ) || "-",
        // },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.from?.name || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.status === "active"}
                onChange={() => {
                  statusChangeHandler({
                    id: item?._id,
                    status:
                      item?.status === "active" ? "deactivated" : "active",
                  });
                }}
              />
              <Badge
                type={item?.status === "active" ? "success" : "danger"}
                label={item?.status}
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: t("Edit"),
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
          ],
        },
      ],
    };
  });

  // add or edit Application Form =================>
  function emailFormManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(
            ["emailtemplate", filters, search],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = [res?.data, ...stateData];
                return { ...state, data: stateData };
              }
            }
          );
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["emailtemplate", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(
          ["emailtemplate", filters, search],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.map((item) =>
                item?._id === res?.data?._id ? { ...item, ...res?.data } : item
              );
              return { ...state, data: stateData };
            }
          }
        );
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              Email Management
            </div>
            <div className="text-sm xl:text-base h-10">
              Easily handle all email actions: update email info, email, status,
              and add new email data.
            </div>
          </div>
          {/* <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              variant="secondary"
              buttonLabel={"Create New"}
              buttonLabelClasses={
                "font-medium !text-sm uppercase whitespace-nowrap"
              }
              buttonHasLink
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-72">
              <Search
                placeholder={"Search here"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Select
                selectedValue={"Buyer"}
                dropdownData={[{ name: "Buyer", value: "Buyer" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
            <div className="relative">
              <Select
                selectedValue={"Past 3 month"}
                dropdownData={[{ name: "Past 3 month", value: "Past 3 month" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
          </div>
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>
      <ManageEmailTemplateDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={emailFormManageResponse}
      />
    </div>
  );
};

export default EmailTemplate;
